import { Mark, mergeAttributes } from '@tiptap/core'

/**
 * Replaces the draft-js PLACEHOLDER entity.
 *
 * Template placeholders the writer must replace before sending. Round-trips as
 * <span class="placeholder">, and carries the `placeholder-blocker` class in
 * the editor so existing styling/selectors keep working.
 */
export const PlaceholderMark = Mark.create({
  name: 'placeholderMark',
  inclusive: false,

  addAttributes() {
    return {
      title: {
        default: null,
        parseHTML: element => element.getAttribute('title'),
        renderHTML: attributes => (attributes.title ? { title: attributes.title } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span.placeholder' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'placeholder placeholder-blocker' }), 0]
  },
})

/**
 * Replaces the draft-js HIDDENLINK entity.
 *
 * The account-page URL is hidden from the writer so they cannot follow it and
 * leave themselves a 5-star review (which would trigger a bonus payment). The
 * real target is parked in `hiddenHref` and the visible href is neutralised to
 * "#"; it is restored only when serialising for send.
 */
export const HiddenLinkMark = Mark.create({
  name: 'hiddenLink',
  inclusive: false,
  // Must out-rank the generic Link mark so that <a class="account-page-url">
  // parses as a HiddenLink and keeps its href neutralised, rather than being
  // picked up as an ordinary followable link. @tiptap/extension-link uses
  // priority 1000 as of v3; re-check this on upgrade. The links test
  // ('still routes the account-page link to HiddenLink') guards it.
  priority: 1100,

  addAttributes() {
    return {
      hiddenHref: {
        default: null,
        // On the way in the real target may be in either attribute depending on
        // whether the HTML came from the API or from a previous editor pass.
        parseHTML: element => element.getAttribute('data-hidden-href') || element.getAttribute('href'),
        renderHTML: attributes => (attributes.hiddenHref ? { 'data-hidden-href': attributes.hiddenHref } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'a.account-page-url' }]
  },

  renderHTML({ HTMLAttributes }) {
    // Never render the real href into the editor DOM.
    return ['a', mergeAttributes(HTMLAttributes, { class: 'account-page-url', href: '#' }), 0]
  },
})
